import type { Building } from "building-types/building/buildingTypes";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import { groundLevelId } from "../elements/level/levelTools";
import { makeExteriorWainscoting, makeHouseWainscoting } from "../elements/wainscoting";
import { generateWainscotingContextNew } from "../elements/wainscoting";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  planarGraphs,
  planarGraphPerimeters,
  planarGraphLevelAssociations,
  levels,
  walls,
  floorPaths,
  rooms,
  blocks,
  labels,
  facades,
  mirror,
  enabledFeatures,
}: Pick<
  Building,
  | "settings"
  | "planarGraphs"
  | "planarGraphPerimeters"
  | "planarGraphLevelAssociations"
  | "levels"
  | "walls"
  | "floorPaths"
  | "rooms"
  | "blocks"
  | "labels"
  | "facades"
  | "mirror"
  | "enabledFeatures"
>) {
  return {
    levels,
    blocks,
    planarGraphs,
    planarGraphPerimeters,
    planarGraphLevelAssociations,
    walls,
    floorPaths,
    wainscotingSettings: settings.wainscoting,
    wallSettings: settings.wall,
    doorSettings: settings.door,
    foundationSettings: settings.foundation,
    rooms,
    labels,
    facades,
    mirror,
    enabledFeatures,
  };
}

const compare = makeGenerationStageComparator<typeof select>();

function generate({
  levels,
  blocks,
  planarGraphs,
  planarGraphPerimeters,
  planarGraphLevelAssociations,
  walls,
  wainscotingSettings,
  wallSettings,
  doorSettings,
  foundationSettings,
  floorPaths,
  rooms,
  labels,
  facades,
  mirror,
  enabledFeatures,
}: ReturnType<typeof select>): { wainscoting: Building["wainscoting"] } {
  const wainscotingContext = generateWainscotingContextNew();
  if (enabledFeatures.wallPlanes) {
    /**
     * @NOTE - Wainscoting is not supported when wall planes are enabled.
     * These are incompatable features.
     */
    return { wainscoting: wainscotingContext.wainscoting };
  }

  const makeLevelWainscoting = (levelId: string) => {
    makeHouseWainscoting({
      level: levels[levelId],
      levels,
      blocks,
      walls,
      planarGraph: planarGraphs[levelId].planarGraph,
      planarGraphInfo: planarGraphPerimeters,
      planarGraphLevelAssociations,
      wainscotingSettings,
      doorSettings,
      labels: labels[levelId],
      mirror,
      facades,
      rooms,
      isLastLevel: levels[levelId].levelAbove === undefined,
      wainscotingContext,
    });

    const porchWainscotingSettings: WainscotingSettings = {
      ...wainscotingSettings,
      wainscotingMode: wainscotingSettings.porchWainscotingMode,
      capType: wainscotingSettings.porchCapType ?? wainscotingSettings.capType,
    };

    if (!floorPaths[levelId]) {
      return;
    }

    makeExteriorWainscoting({
      level: levels[levelId],
      levels,
      blocks,
      wallSystem: walls[levelId],
      floorPaths: floorPaths[levelId],
      coveredPerimeterFaces: planarGraphPerimeters[levelId].coveredPerimeterFaces,
      uncoveredPerimeterFaces: planarGraphPerimeters[levelId].uncoveredPerimeterFaces,
      planarGraph: planarGraphs[levelId].planarGraph,
      wainscotingSettings: porchWainscotingSettings,
      wallSettings,
      foundationSettings,
      labels: labels[levelId],
      mirror,
      facades,
      rooms,
      isLastLevel: levels[levelId].levelAbove === undefined,
      wainscotingContext,
    });
  };

  // we need to draw the ground level wainscoting first as a reference
  const refLevelId = groundLevelId(levels);
  if (refLevelId) {
    makeLevelWainscoting(refLevelId);
  }

  for (const levelId in levels) {
    if (levelId !== refLevelId) {
      makeLevelWainscoting(levelId);
    }
  }

  return { wainscoting: wainscotingContext.wainscoting };
}

export default memo(select, generate, compare);
